import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import api from '../utils/api';
import './ApplicationListPage.css';

const CATEGORY_LABELS = {
  health: '🏥 Health',
  education: '🎓 Education',
  culture: '🌿 Culture',
  legal: '⚖️ Legal',
};

const HealthApplicationListPage = () => {
  const { currentUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [programFilter, setProgramFilter] = useState('all');

  useEffect(() => {
    if (currentUser && !(currentUser.health || currentUser.education || currentUser.role === 'admin' || currentUser.role === 'staff')) {
      navigate('/staff-dashboard');
    }
  }, [currentUser, navigate]);

  useEffect(() => {
    fetchApplications();
    fetchPrograms();
  }, []);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/health-program-applications');
      setApplications(response.data.applications || []);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching applications');
      setLoading(false);
    }
  };

  const fetchPrograms = async () => {
    try {
      const response = await api.get('/api/health-programs');
      setPrograms(response.data.programs || []);
    } catch (err) {
      console.error('Error fetching programs:', err);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'submitted': return 'status-badge status-submitted';
      case 'reviewed': return 'status-badge status-reviewed';
      case 'approved': return 'status-badge status-approved';
      case 'rejected': return 'status-badge status-rejected';
      case 'needsmoreinfo': return 'status-badge status-needsmoreinfo';
      case 'waitlisted': return 'status-badge status-waitlisted';
      default: return 'status-badge';
    }
  };

  const mapStatus = (status) => {
    switch (status) {
      case 'submitted': return 'Submitted';
      case 'reviewed': return 'In Review';
      case 'approved': return 'Approved';
      case 'rejected': return 'Rejected';
      case 'needsMoreInfo': return 'Needs More Info';
      case 'waitlisted': return 'Waitlisted';
      default: return status;
    }
  };

  const handleDeleteApplication = async (appId) => {
    if (!window.confirm('Are you sure you want to delete this application? This action cannot be undone.')) {
      return;
    }

    try {
      await api.delete(`/api/health-program-applications/${appId}`);
      setApplications(applications.filter(app => app._id !== appId));
      alert('Application deleted successfully');
    } catch (err) {
      alert(err.response?.data?.message || 'Error deleting application');
    }
  };

  // Derive available categories and program types from data
  const availableCategories = [...new Set(applications.map(a => a.portal).filter(Boolean))].sort();
  const availableTypes = [...new Set(
    applications.flatMap(a => Array.isArray(a.programType) ? a.programType : (a.programType ? [a.programType] : []))
  )].sort();

  let filteredApplications = applications;

  if (statusFilter !== 'all') {
    filteredApplications = filteredApplications.filter(app =>
      app.status?.toLowerCase() === statusFilter.toLowerCase()
    );
  }
  if (categoryFilter !== 'all') {
    filteredApplications = filteredApplications.filter(app => app.portal === categoryFilter);
  }
  if (typeFilter !== 'all') {
    filteredApplications = filteredApplications.filter(app =>
      Array.isArray(app.programType)
        ? app.programType.includes(typeFilter)
        : app.programType === typeFilter
    );
  }
  if (programFilter !== 'all') {
    filteredApplications = filteredApplications.filter(app =>
      (app.healthProgramId?._id || app.healthProgramId) === programFilter
    );
  }

  const stats = {
    total: applications.length,
    submitted: applications.filter(a => a.status === 'submitted').length,
    reviewed: applications.filter(a => a.status === 'reviewed').length,
    approved: applications.filter(a => a.status === 'approved').length,
    rejected: applications.filter(a => a.status === 'rejected').length,
    needsMoreInfo: applications.filter(a => a.status === 'needsMoreInfo').length,
    waitlisted: applications.filter(a => a.status === 'waitlisted').length
  };

  const categoryStats = availableCategories.map(cat => ({
    key: cat,
    label: CATEGORY_LABELS[cat] || cat,
    count: applications.filter(a => a.portal === cat).length
  }));

  return (
    <div className="application-list-container">
      <div className="page-header">
        <h1>📋 Program Applications</h1>
        <button className="btn btn-secondary" onClick={() => navigate('/staff-health-programs')}>
          ← Back to Programs
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-number">{stats.total}</div>
          <div className="stat-label">Total</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{stats.submitted}</div>
          <div className="stat-label">Submitted</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{stats.reviewed}</div>
          <div className="stat-label">In Review</div>
        </div>
        <div className="stat-card stat-approved">
          <div className="stat-number">{stats.approved}</div>
          <div className="stat-label">Approved</div>
        </div>
        <div className="stat-card stat-rejected">
          <div className="stat-number">{stats.rejected}</div>
          <div className="stat-label">Rejected</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{stats.waitlisted}</div>
          <div className="stat-label">Waitlisted</div>
        </div>
      </div>

      {categoryStats.length > 0 && (
        <div className="category-tabs">
          <button
            className={`category-tab${categoryFilter === 'all' ? ' active' : ''}`}
            onClick={() => setCategoryFilter('all')}
          >
            All Categories <span className="tab-count">{applications.length}</span>
          </button>
          {categoryStats.map(cat => (
            <button
              key={cat.key}
              className={`category-tab${categoryFilter === cat.key ? ' active' : ''}`}
              onClick={() => setCategoryFilter(cat.key)}
            >
              {cat.label} <span className="tab-count">{cat.count}</span>
            </button>
          ))}
        </div>
      )}

      <div className="filter-controls">
        <div className="filter-group">
          <label htmlFor="status-filter">Status:</label>
          <select
            id="status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Statuses</option>
            <option value="submitted">Submitted</option>
            <option value="reviewed">In Review</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="needsMoreInfo">Needs More Info</option>
            <option value="waitlisted">Waitlisted</option>
          </select>
        </div>

        {availableTypes.length > 0 && (
          <div className="filter-group">
            <label htmlFor="type-filter">Category Type:</label>
            <select
              id="type-filter"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <option value="all">All Types</option>
              {availableTypes.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
        )}

        <div className="filter-group">
          <label htmlFor="program-filter">Program:</label>
          <select
            id="program-filter"
            value={programFilter}
            onChange={(e) => setProgramFilter(e.target.value)}
          >
            <option value="all">All Programs</option>
            {programs.map(prog => (
              <option key={prog._id} value={prog._id}>{prog.title}</option>
            ))}
          </select>
        </div>
      </div>
      
      {loading ? (
        <div className="loading-indicator">
          <div className="spinner"></div>
          <p>Loading applications...</p>
        </div>
      ) : applications.length === 0 ? (
        <div className="empty-state">
          <p>No applications found.</p>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="applications-table">
            <thead>
              <tr>
                <th>Applicant</th>
                <th>Category</th>
                <th>Program</th>
                <th>Program Category</th>
                <th>Submitted</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredApplications.length === 0 ? (
                <tr>
                  <td colSpan="7" className="no-results">No applications match the selected filters</td>
                </tr>
              ) : (
                filteredApplications.map(app => (
                  <tr key={app._id}>
                    <td>{app.applicantName}</td>
                    <td>
                      <span className={`portal-badge portal-${app.portal}`}>
                        {CATEGORY_LABELS[app.portal] || app.portal || '—'}
                      </span>
                    </td>
                    <td>{app.programTitle || 'N/A'}</td>
                    <td>
                      {Array.isArray(app.programType)
                        ? app.programType.join(', ')
                        : (app.programType || '—')}
                    </td>
                    <td>{formatDate(app.dateSubmitted)}</td>
                    <td>
                      <span className={getStatusBadgeClass(app.status)}>
                        {mapStatus(app.status)}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons-cell">
                        <button 
                          className="table-action-btn btn-review" 
                          onClick={() => navigate(`/review-health-application/${app._id}`)}
                        >
                          Review
                        </button>
                        <button 
                          className="table-action-btn btn-delete" 
                          onClick={() => handleDeleteApplication(app._id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default HealthApplicationListPage;
